import React, { useState, useCallback, useRef, useEffect, createContext, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { DynamicDataSheetGrid, keyColumn, textColumn } from 'react-datasheet-grid';
import type { CellComponent, Column } from 'react-datasheet-grid';
import upperFirst from 'lodash/upperFirst';
import cloneDeep from 'lodash/cloneDeep';

import { Tooltip, Typography } from '@mui/material';

import { scrollableMixin } from '@/common';

import 'react-datasheet-grid/dist/style.css';
import Icon from "@imminently/decisively-ui/Icon";
import CellTooltip from "@imminently/decisively-ui/Tooltip";
import { useAvailableHeight } from './useAvailableHeight';

const cellTextFromRowData = (rowData: unknown): string => {
  if (typeof rowData === 'string' || typeof rowData === 'number') return String(rowData);
  return '';
};

/**
 * Wraps any column's cell component so hovering it shows the cell's full value in a tooltip -
 * useful once a narrow column truncates its content with CSS ellipsis. Preserves the wrapped
 * column's own behaviour (editable or read-only) unchanged; only text/number values get a
 * tooltip; other cell types are rendered as-is with no tooltip added.
 */
export const withCellTooltip = <T, C>(column: Partial<Column<T, C, any>>): Partial<Column<T, C, any>> => {
  const OriginalComponent = column.component;
  if (!OriginalComponent) return column;

  const TooltipCell: CellComponent<T, C> = (props) => {
    const text = cellTextFromRowData(props.rowData);
    return (
      <CellTooltip
        title={text}
        enterDelay={500}
        asChild
      >
        <div style={{ display: 'flex', flex: 1, minWidth: 0, alignSelf: 'stretch' }}>
          <OriginalComponent {...props} />
        </div>
      </CellTooltip>
    );
  };

  return { ...column, component: TooltipCell };
};

const useDocumentEventListener = (
  type,
  listener,
) => {
  useEffect(() => {
    document.addEventListener(type, listener);

    return () => {
      document.removeEventListener(type, listener);
    };
  }, [listener, type]);
};

const Actions = {
  INSERT_ROW: 'insert-row',
  INSERT_COLUMN: 'insert-column',
  DELETE_COLUMN: 'delete-column',
};

const createRow = () => ({});
const createColumnDefault = col => ({ ...keyColumn(`col${col}`, textColumn), title: 'Column' });


const reducer = (state, action) => {
  const { type, payload } = action;
  const { columns, data } = state;
  switch (type) {
    case Actions.INSERT_ROW:
      return {
        columns,
        data: [
          ...data.slice(0, payload.row),
          payload.data,
          ...data.slice(payload.row),
        ],
      };
    case Actions.INSERT_COLUMN:
      return {
        columns: [
          ...columns.slice(0, payload.col),
          payload.data,
          ...columns.slice(payload.col),
        ],
        data,
      };
    case Actions.DELETE_COLUMN:
      const id = columns[payload.col].id;
      return {
        columns: [
          ...columns.slice(0, payload.col),
          ...columns.slice(payload.col + 1),
        ],
        data: cloneDeep(data).map(d => {

          delete d[id];

          return d;
        }),
      };
    case 'SET':
      return { columns, data: payload };
    default:
      return state;
  }
};

const SheetContext = createContext<any>(null);
const useSheet = () => {
  const context = useContext(SheetContext);
  return context?.current;
};

const StyledDataSheet = styled(DynamicDataSheetGrid)`
  .dsg-container {
    border: none;
    overflow: overlay !important;
    ${scrollableMixin};
    /* border-bottom: 1px solid var(--border); */
  }

  .dsg-row-header {
    border: 1px solid var(--border);
    border-bottom: none;
    /* border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem; */
    background: black;
    box-shadow: none;

    .dsg-corner-indicator {
      display: none;
    }

    .dsg-cell {
      color: var(--light);
      border: none;
      background: unset;
      border-bottom: 1px solid var(--border);
      /* border-bottom: none; */

      &.dsg-cell-gutter {
        box-shadow: none;
        background: black;

      }

      &:not(:last-child) {
        border-right: 1px solid var(--border);
      }

      .dsg-cell-header-container {
        flex: 1;
      }
    }
  }

  .dsg-row:not(.dsg-row-header) {
    box-shadow: none;
    /* &:nth-child(${(props: any) => props.rows + 1}) {
      .dsg-cell {
        &:first-child {
          border-bottom-left-radius: 0.5rem;
        }
        &:last-child {
          border-bottom-right-radius: 0.5rem;
        }
      }
    } */
    /* &:not(:last-child) { */
    border-bottom: 1px solid var(--border);
    /* } */

    .dsg-cell {
      box-shadow: none;
      border-top: none;

      &:last-child {
        border-right: 1px solid var(--border);
      }
    }
  }

  .dsg-cell-condition {
    background: var(--secondary-hover);
    position: relative;

    &+ .dsg-cell-conclusion {
      position: relative;
      :after {
        position: absolute;
        content: '';
        height: 100%;
        width: 2px;
        left: -1px;
        background-color: var(--secondary-dark);
      }
    }
  }

  .dsg-add-row {
    display: none;
  }

  /* Truncate overflowing cell text with an ellipsis rather than a hard clip - pairs with
     withCellTooltip's hover tooltip, which shows the full value that got cut off here. */
  .dsg-input {
    text-overflow: ellipsis;
  }

  .dsg-context-menu {
    display: flex;
    flex-flow: row nowrap;
    gap: 0.25rem;
    padding: 0.25rem 1rem;
    border-radius: 0.5rem;
    box-shadow: ${props => props.theme.mixins.body.boxShadow};

    > .dsg-context-menu-item {
      max-height: 2rem;
      padding: 0.25rem;
      border-radius: 50%;
      cursor: pointer;

      &:hover {
        background: var(--secondary-hover);
      }
    }
  }
`;

const gutter = {
  component: ({ rowIndex }: any) => <>{`${rowIndex + 1}.`}</>,
};

const renderItem = item => {
  if (item.type === 'DELETE_ROW') {
    return <Icon name="delete-row" />;
  }

  if (item.type === 'DELETE_ROWS') {
    return (
      <>
        Delete rows
        {' '}
        <b>{item.fromRow}</b>
        {' '}
        to
        {' '}
        <b>{item.toRow}</b>
      </>
    );
  }

  if (item.type === 'INSERT_ROW_BELOW') {
    return <Icon name="add-row-below" />;
  }

  if (item.type === 'DUPLICATE_ROW') {
    return 'Duplicate row';
  }

  if (item.type === 'DUPLICATE_ROWS') {
    return (
      <>
        {'Duplicate rows '}
        <b>{item.fromRow}</b>
        {' to '}
        <b>{item.toRow}</b>
      </>
    );
  }

  return item.type;
};

const SUPPORTED = ['INSERT_ROW_BELLOW', 'DELETE_ROW'];
// const UNSUPPORTED = [ 'DUPLICATE_ROW', 'DUPLICATE_ROWS', 'DELETE_ROWS' ];

export const ContextMenu = ({
  clientX,
  clientY,
  items,
  close,
  state,
  dispatch,
  staticColumns,
}) => {
  const containerRef = useRef<any>(null);
  const { current: sheet } = useContext(SheetContext) as any;

  const onClickOutside = useCallback(
    event => {
      const clickInside = (containerRef.current as any)?.contains(event.target);

      if (!clickInside) {
        close();
      }
    },
    [close],
  );
  useDocumentEventListener('mousedown', onClickOutside);

  const isMultiRow = () => {
    const { min, max } = sheet.selection;
    return min.row !== max.row;
  };

  const isMultiCol = () => {
    const { min, max } = sheet.selection;
    return min.col !== max.col;
  };

  const column = state.columns[sheet.activeCell.col];
  const rowData = state.data[sheet.activeCell.row];
  const createColumn = column.createColumn ?? createColumnDefault;
  const canDeleteColumn = column.canDeleteColumn ?? (() => state.columns.length > 1);
  const canDeleteRow = column.canDeleteRow ?? (() => state.data.length > 1);
  console.log('context', sheet, column, rowData);

  const columnActions = [
    {
      type: 'INSERT_COLUMN_LEFT',
      icon: <Icon name="add-column-left" />,
      action: () => {
        close();
        const col = isMultiCol() ? sheet.selection.min.col : sheet.activeCell.col;
        dispatch({
          type: Actions.INSERT_COLUMN,
          payload: {
            col,
            data: createColumn(col),
          },
        });
      },
    },
    {
      type: 'INSERT_COLUMN_RIGHT',
      icon: <Icon name="add-column-right" />,
      action: () => {
        close();
        const col = isMultiCol() ? sheet.selection.max.col : sheet.activeCell.col;
        dispatch({
          type: Actions.INSERT_COLUMN,
          payload: {
            col: col + 1,
            data: createColumn(col),
          },
        });
      },
    },
    !isMultiCol() && canDeleteColumn(column) && {
      type: 'DELETE_COLUMN',
      icon: <Icon name="delete-column" />,
      action: () => {
        close();
        const col = sheet.activeCell.col;
        sheet.setSelection(null);
        dispatch({
          type: Actions.DELETE_COLUMN,
          payload: {
            col,
          },
        });
        setTimeout(() => sheet.setActiveCell({
          col: col - 1,
          row: sheet.activeCell?.row || 0,
          doNotScrollX: true,
        }), 0);
      },
    },
    {
      type: 'SPACE',
    },
  ].filter(a => !!a);

  let menu = [
    ...(staticColumns ? [] : columnActions),
    {
      type: 'INSERT_ROW_ABOVE',
      icon: <Icon name="add-row-top" />,
      action: () => {
        close();
        const row = isMultiRow() ? sheet.selection.min.row : sheet.activeCell.row;
        const count = 1;

        dispatch({
          type: Actions.INSERT_ROW,
          payload: {
            row,
            data: createRow(),
          },
        });

        setTimeout(() => sheet.setActiveCell({
          col: sheet.activeCell?.col || 0,
          row: row + count,
          doNotScrollX: true,
        }), 0);
      },
    },
    ...items
      .filter(i => SUPPORTED.includes(i.type))
      .filter(i => i.type !== 'DELETE_ROW' || canDeleteRow({ rowData }))
      .map(i => {
        if (i.type.indexOf('BELLOW') !== -1) {
          // fix incorrect spelling of default action
          return { ...i, type: 'INSERT_ROW_BELOW' };
        }

        return i;
      }),
  ];

  if (column.contextMenuOverride) {
    menu = column.contextMenuOverride({ menu, column, rowData, close });
  }

  return (
    <div
      className='dsg-context-menu'
      style={{ left: `${clientX}px`, top: `${clientY}px` }}
      ref={containerRef}
    >
      {menu.map((item, i) => (
        item.type === 'SPACE'
          ? <div key={`SPACE-${i}`} style={{ width: '1rem' }} />
          : (
            <div
              key={item.type}
              onClick={item.action}
              className='dsg-context-menu-item'
            >
              <Tooltip title={upperFirst(item.type.split('_').join(' ').toLowerCase())}>
                <span>
                  {item.icon || renderItem(item)}
                </span>
              </Tooltip>
            </div>
          )
      ))}
    </div>
  );
};

const DefaultCtxWrapComp = ({ children, ...props }) => (
  <div {...props}>{children}</div>
);

export const DataSheetWithCtxWrapComp = styled.div`
  overflow: auto;

  >* {
    height: 100%;

    .dsg-container {
      height: 100% !important;
    }
  }
`;

const withContext = (Component: any) => ({ CtxWrapComp = DefaultCtxWrapComp, ...props }: any) => {
  const ref = useRef<any>(null);
  const elem = useRef<any>(null);
  const [sheet, setSheet] = useState<any>(null);
  const height = useAvailableHeight(elem);

  useEffect(() => {
    if (ref) {
      setSheet(ref);
    }
  }, [ref, setSheet]);

  return (
    // <DataContextProvider initialState={{ columns, data }}>
    // </DataContextProvider>
    <SheetContext.Provider value={sheet}>
      <CtxWrapComp ref={elem}>
        <Component ref={ref} height={height} {...props} />
      </CtxWrapComp>
    </SheetContext.Provider>
  );
};

const Sheet = ({ data, columns, onChange, staticColumns = false, height, ref, ...props }: any) => {
  // const [state, dispatch] = useDataContext();
  const state = useMemo(() => ({ data, columns }), [data, columns]);

  const dispatch = useCallback(action => {
    onChange(reducer(state, action));
  }, [state, onChange]);

  const setData = useCallback((payload, changes) => {
    console.log('setData change', changes, data);
    const change = changes[0];
    if (change.type === 'DELETE') {
      const rowData = data[change.fromRowIndex];
      console.log('deleted row', rowData);
    }

    dispatch({ type: 'SET', payload });
  }, [dispatch, data]);

  // useDeepCompareEffect(() => {
  //   onChange?.(state);
  // }, [state, onChange]);

  const MyContextMenu = useCallback((props: any) => (
    <ContextMenu staticColumns={staticColumns} state={state} dispatch={dispatch} {...props} />
  ), [staticColumns, state, dispatch]);

  const onKeyDown = event => {
    const { activeCell, setActiveCell } = ref?.current || { activeCell: { col: 0 } };
    // console.log('onKeyDown', event, ref);
    if (
      !staticColumns &&
      event.key === 'C' &&
      event.shiftKey &&
      (event.ctrlKey ||
        event.metaKey) &&
      !event.altKey
    ) {
      event.preventDefault();
      // console.log('insert new column', activeCell);
      const column = state.columns[activeCell.col];
      const createColumn = column.createColumn ?? createColumnDefault;
      dispatch({ type: Actions.INSERT_COLUMN, payload: { col: activeCell.col + 1, data: createColumn(activeCell.col) } });

      setTimeout(() => setActiveCell?.({
        col: activeCell.col + 1,
        row: activeCell.row || 0,
      }), 0);
    }
  };

  useDocumentEventListener('keydown', onKeyDown);

  if (!data) {
    return (
      <Typography>Error processing sheet</Typography>
    )
  }

  return (
    <StyledDataSheet as={DynamicDataSheetGrid as any}
      ref={ref}
      height={height}
      rows={data.length}
      value={data}
      onChange={setData}
      columns={columns}
      createRow={createRow}
      gutterColumn={gutter}
      contextMenuComponent={MyContextMenu}
      {...props}
    />
  );
};
Sheet.displayName = 'DataSheet';

export const DataSheet = withContext(Sheet);
